import { useMemo, type ReactNode } from "react";
import { useDebounce } from "@reactuses/core";
import { useTranslation } from "react-i18next";
import { useBlockWindowQuery } from "../../components/table/useBlockWindowQuery";
import { type ColumnFiltersState } from "@tanstack/react-table";
import { type ManagedTableState, type ManagedTableStateSetters } from "../../components/table/usePersistedTableState";
import { RT8ColumnSelector } from "../../components/core/blocks/MetricSelector";
import { SearchInput } from "../../components/fields/base/TextField";
import { RT8Pagination } from "../../components/table/Pagination";
import { Table } from "../../components/table/Table";
import { resetTable } from "../../components/table/utils";
import { RT8FilterWidget } from "../merchandising/components/tables/FilterWidget";
import { DataViewPicker } from "../dataViews/components/DataViewPicker";
import { Button } from "../../components/core/basic/Button";
import { MdRotateLeft } from "react-icons/md";
import { TableErrorWrapper } from "../../components/table/TableErrorWrapper";
import {
  type SearchQueryPerformanceV4Row,
  useGetV4SearchQueryPerformanceByQuery,
} from "./api/v4SearchQueryPerformanceReportByQuery";
import { Flex, HStack, VStack } from "../../../styled-system/jsx";
import { css } from "../../../styled-system/css";
import { type Moment } from "moment";
import { SearchQueryRowExpander } from "./SearchQueryRowExpander";
import useSearchTermReport from "../../hooks/useSearchTermReport";
import { useDates } from "../../dates/DatesContext";
import Loading from "../../components/core/blocks/Loading";
import { DownloadV4Report } from "../perf_reports/DownloadReport";
import { useV4SearchQueryPerformanceReportExport } from "./api/v4SearchQueryPerformanceReportExport";
import { useServerTable, type AppColumns } from "../../components/table/tableFeatures";

const Expander = ({
  row,
  start,
  end,
  preStart,
  preEnd,
}: {
  start: Moment;
  end: Moment;
  preStart: Moment;
  preEnd: Moment;
  row: {
    original: SearchQueryPerformanceV4Row;
  };
}) => {
  const { data: currentData, isLoading } = useSearchTermReport(
    row.original.search_query,
    start.format("YYYY-MM-DD"),
    end.format("YYYY-MM-DD"),
    1000,
    "query",
    {
      overrideFilterType: "iexact",
    },
  );

  const queryData = useMemo(() => {
    const matchingRow = currentData?.find(
      (strRow: { query: string; adv_targets: number[]; adv_keywords: number[] }) =>
        strRow.query === row.original.search_query,
    );

    return {
      ...row.original,
      adv_targets: matchingRow?.adv_target_ids?.filter(Boolean) ?? [],
      adv_keywords: matchingRow?.adv_keyword_ids?.filter(Boolean) ?? [],
    };
  }, [currentData, row]);

  // How to join expander => it used to use the SQPR ID as a filter, then the STR to retrieve advertised keywords and targets
  return isLoading ?
      <Loading fullPage={false} height={"300px"} />
    : <SearchQueryRowExpander
        row={queryData}
        selectedReport={{
          start_date: start.format("YYYY-MM-DD"),
          end_date: end.format("YYYY-MM-DD"),
        }}
        selectedCompareReport={{
          start_date: preStart.format("YYYY-MM-DD"),
          end_date: preEnd.format("YYYY-MM-DD"),
        }}
      />;
};

const SearchQueryPerformanceDashboardTable = ({
  columns,
  state,
  initialState,
  setters,
  selectedAsins,
  setSelectedAsins,
  setShowAsinFilter,
  renderAsinsFilter = true,
  renderExpander = true,
  showDownloadButton = false,
  downloadButtonText,
  downloadFileName = "report.xlsx",
}: {
  columns: AppColumns<SearchQueryPerformanceV4Row>;
  state: ManagedTableState;
  initialState: ManagedTableState;
  setters: ManagedTableStateSetters;
  selectedAsins: { product_id: string; asin: string }[];
  setSelectedAsins?: (ids: { product_id: string; asin: string; sku: string | null }[]) => void;
  setShowAsinFilter?: (show: boolean) => void;
  renderAsinsFilter?: boolean;
  renderExpander?: boolean;
  showDownloadButton?: boolean;
  downloadButtonText?: ReactNode;
  downloadFileName?: string;
}) => {
  const { t } = useTranslation(["sqpr", "common"]);
  const debounceSearch = useDebounce(state.globalFilter, 500);

  const bySearchQueryOptionsGetter = useGetV4SearchQueryPerformanceByQuery();

  const filters = useMemo<ColumnFiltersState>(() => {
    const asinFilter: ColumnFiltersState =
      selectedAsins.length ?
        [
          {
            id: "asins",
            value: { op: "anyof", value: selectedAsins.map((asin) => asin.asin) },
          },
        ]
      : [];

    return [...state.columnFilters, ...asinFilter];
  }, [state.columnFilters, selectedAsins]);

  const exportMutation = useV4SearchQueryPerformanceReportExport(filters, debounceSearch);

  const searchQueryPerformanceByQueryReportQuery = useBlockWindowQuery(bySearchQueryOptionsGetter, {
    pagination: state.pagination,
    sorting: state.sorting,
    filters,
    search: debounceSearch,
  });
  const { start, end, preStart, preEnd } = useDates();

  const formattedData: SearchQueryPerformanceV4Row[] = useMemo(() => {
    if (!searchQueryPerformanceByQueryReportQuery.data) {
      return [];
    }

    return searchQueryPerformanceByQueryReportQuery.data.data;
  }, [searchQueryPerformanceByQueryReportQuery.data]);

  const table = useServerTable({
    data: formattedData,
    columns,
    getRowCanExpand: () => renderExpander,
    getRowId: (row) => row.search_query,
    enableRowSelection: true,
    rowCount: searchQueryPerformanceByQueryReportQuery.data?.count || 0,
    initialState: initialState,
    state: state,
    ...setters,
    alternateSortingEnabled: true,
    meta: {
      showSkeleton:
        searchQueryPerformanceByQueryReportQuery.isFetching &&
        (!searchQueryPerformanceByQueryReportQuery.data || searchQueryPerformanceByQueryReportQuery.isPlaceholderData),
      columnFilterSelectOptions: {},
    },
  });

  return (
    <div>
      <HStack padding={"small"} fontSize={"large"}>
        <VStack flex="1" alignItems={"start"}>
          <HStack width={"100%"}>
            <Button
              variant={"contentOnly"}
              onPress={() => resetTable(table, "searchQueryPerformanceReport")}
              fontSize={"xxlarge"}
            >
              <MdRotateLeft />
            </Button>
            <RT8ColumnSelector table={table} />
            <DataViewPicker viewKey="searchQueryPerformanceReport" datasetKey="sqpr" table={table} />
            <SearchInput
              aria-label={t(($) => $.common.actions.Search)}
              isExpandable
              css={{ flex: "0 1 350px" }}
              value={table.state.globalFilter}
              onChange={table.setGlobalFilter}
            />
          </HStack>
          <RT8FilterWidget table={table} />
        </VStack>
        {renderAsinsFilter && setShowAsinFilter && (
          <VStack justifyContent={"center"}>
            <Button onPress={() => setShowAsinFilter(true)}>{t(($) => $.sqpr["Filter by Product"])}</Button>
            {selectedAsins.length ?
              <Flex gap={"medium"} alignItems={"center"}>
                <span className={css({ fontSize: "medium" })}>
                  {t(($) => $.sqpr["ASINs Selected"], {
                    count: selectedAsins.length,
                  })}
                </span>
                <Button
                  variant={"link"}
                  className={css({ fontSize: "medium" })}
                  onPress={() => {
                    setSelectedAsins?.([]);
                  }}
                >
                  {t(($) => $.common.actions.Clear)}
                </Button>
              </Flex>
            : null}
          </VStack>
        )}
        <RT8Pagination className={css({ marginLeft: "auto" })} table={table} />
      </HStack>
      <TableErrorWrapper
        error={searchQueryPerformanceByQueryReportQuery.error}
        userMessage={t(($) => $.sqpr["Error loading stats"])}
      >
        <Table
          table={table}
          className={css({ minWidth: "100%" })}
          renderExpander={
            renderExpander === false ? undefined : (
              (row) => {
                return <Expander row={row} preStart={preStart} preEnd={preEnd} end={end} start={start} />;
              }
            )
          }
        />
      </TableErrorWrapper>
      {showDownloadButton && (
        <HStack width={"100%"} padding={"small"}>
          <DownloadV4Report downloadMutation={exportMutation} fileName={downloadFileName}>
            {downloadButtonText ?? t(($) => $.sqpr["Download Report"])}
          </DownloadV4Report>
        </HStack>
      )}
    </div>
  );
};

export default SearchQueryPerformanceDashboardTable;
